/**
 * Cliente HTTP para el backend nuevo de psi-platform (psiplatform-be).
 *
 * Idéntico a secureHttp en comportamiento (Bearer, toasts, reintentos) pero
 * apunta a PSI_PLATFORM_BE_URL en vez de API_CORE_URL, y NO envía x-api-key
 * (el nuevo backend autentica solo con el Bearer del scope psi-platform).
 *
 * Uso: igual que secureHttp.
 *   beHttp.get('/api/psychologist/profile')
 *   beHttp.put('/api/psychologist/profile', body, { successMessage: '...' })
 */

import { PSI_PLATFORM_BE_URL, API_CORE_CONFIG } from './config';
import { getValidToken, redirectToLogin, hasValidSession } from './auth';
import { toastUtils } from '../useToast';

import { newRequestId } from '../../utils/requestId';
import { trackNetworkFailure, trackHttpStatus, rememberRequest, type RequestContext } from '../../utils/activityHttpErrors';
export type QueryParams = Record<string, unknown>;

export interface BeRequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  successMessage?: string;
  errorMessage?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  loadingMessage?: string;
}

function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) usp.set(key, String(value));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

function createTimeoutController(timeoutMs: number): AbortController {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller;
}

async function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function beRequest(
  path: string,
  options: BeRequestOptions = {},
): Promise<Response> {
  const {
    timeout = API_CORE_CONFIG.TIMEOUT,
    retries = API_CORE_CONFIG.MAX_RETRIES,
    ...init
  } = options;

  if (!hasValidSession()) {
    redirectToLogin();
    throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
  }

  const token = getValidToken();
  if (!token) {
    redirectToLogin();
    throw new Error('No hay token válido. Por favor, inicia sesión nuevamente.');
  }

  const url = `${PSI_PLATFORM_BE_URL}${path}`;
  const headers = new Headers(init?.headers || {});

  // FormData (uploads multipart) necesita que el browser ponga su propio
  // Content-Type con el boundary — si lo fijamos nosotros a mano, el request
  // queda mal formado y el backend no puede parsear los archivos.
  if (!headers.has('Content-Type') && !(init?.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  // Solo Bearer — sin x-api-key
  headers.set('Authorization', `Bearer ${token}`);

  const makeRequest = async (attempt: number): Promise<Response> => {
    const controller = createTimeoutController(timeout);
    // Un id por intento: el backend lo loguea en sus 5xx y Actividad lo guarda junto al error. Solo a psiplatform-be:
    // a api-core NO se le manda un header propio (no controlamos su CORS: uno no permitido rompería las llamadas).
    const requestId = newRequestId();
    headers.set('x-request-id', requestId);
    const ctx: RequestContext = { client: 'psiplatform-be', method: init?.method, url, startedAt: Date.now(), retried: attempt > 0, requestId };
    try {
      const response = await fetch(url, { ...init, headers, signal: controller.signal });
      rememberRequest(response, ctx);
      return response;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          trackNetworkFailure('timeout', ctx);
          throw new Error(`Timeout: La request tardó más de ${timeout}ms`);
        }
        if (
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError')
        ) {
          if (attempt < retries) {
            await delay(API_CORE_CONFIG.RETRY_DELAY * Math.pow(2, attempt));
            return makeRequest(attempt + 1);
          }
          trackNetworkFailure('network_error', ctx);
          throw new Error('Error de conexión: No se pudo conectar con el servidor');
        }
      }
      throw error;
    }
  };

  return makeRequest(0);
}

async function parseJson<T>(resp: Response): Promise<T> {
  if (resp.status === 304) return [] as unknown as T;

  const raw = await resp.text();

  if (!resp.ok) {
    trackHttpStatus(resp.status, resp); // Métricas de Actividad: solo 5xx y 401/403 (código cerrado)
    if (resp.status === 401 || resp.status === 403) {
      redirectToLogin();
      throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
    }
    let errorMessage = `Error HTTP ${resp.status}`;
    try {
      const d = JSON.parse(raw);
      if (d?.message) errorMessage = d.message;
      else if (d?.error) errorMessage = d.error;
    } catch {
      errorMessage = raw || errorMessage;
    }
    switch (resp.status) {
      case 400: throw new Error(`Datos inválidos: ${errorMessage}`);
      case 404: throw new Error('Recurso no encontrado');
      case 429: throw new Error('Demasiadas solicitudes. Intenta más tarde');
      case 500: case 502: case 503:
        throw new Error('Error del servidor. Intenta más tarde');
      default: throw new Error(errorMessage);
    }
  }

  if (!raw || raw.trim().length === 0) return [] as unknown as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return raw as unknown as T;
  }
}

async function withToasts<T>(
  fn: () => Promise<T>,
  opts: BeRequestOptions,
): Promise<T> {
  const {
    successMessage,
    errorMessage,
    showSuccessToast = false,
    showErrorToast = true,
    loadingMessage,
  } = opts;

  let toastId: string | number | undefined;
  try {
    if (loadingMessage) toastId = toastUtils.loading(loadingMessage);
    const result = await fn();
    if (toastId) toastUtils.dismiss(toastId);
    if (showSuccessToast && successMessage) toastUtils.success(successMessage);
    return result;
  } catch (error) {
    if (toastId) toastUtils.dismiss(toastId);
    const msg = error instanceof Error ? error.message : 'Error desconocido';
    if (showErrorToast) toastUtils.error(errorMessage || msg);
    throw error;
  }
}

export async function beGet<T>(
  path: string,
  params?: QueryParams,
  options?: BeRequestOptions,
): Promise<T> {
  return withToasts(async () => {
    const q = buildQuery(params);
    const resp = await beRequest(`${path}${q}`, { ...options, method: 'GET' });
    return parseJson<T>(resp);
  }, options || {});
}

export async function bePost<T, B = unknown>(
  path: string,
  body?: B | FormData,
  options?: BeRequestOptions,
): Promise<T> {
  return withToasts(async () => {
    const resp = await beRequest(path, {
      ...options,
      method: 'POST',
      // FormData (uploads) va tal cual — solo el body JSON se serializa.
      body:
        body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
    return parseJson<T>(resp);
  }, options || {});
}

export async function bePut<T, B = unknown>(
  path: string,
  body?: B,
  options?: BeRequestOptions,
): Promise<T> {
  return withToasts(async () => {
    const resp = await beRequest(path, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
    return parseJson<T>(resp);
  }, options || {});
}

export async function bePatch<T, B = unknown>(
  path: string,
  body?: B,
  options?: BeRequestOptions,
): Promise<T> {
  return withToasts(async () => {
    const resp = await beRequest(path, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
    return parseJson<T>(resp);
  }, options || {});
}

export async function beDelete<T, B = unknown>(
  path: string,
  body?: B,
  options?: BeRequestOptions,
): Promise<T> {
  return withToasts(async () => {
    const resp = await beRequest(path, {
      ...options,
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
    return parseJson<T>(resp);
  }, options || {});
}

/**
 * Parseo para las llamadas públicas: NO redirige a login en 401/403 (no hay sesión que
 * renovar) y devuelve `undefined` con body vacío en vez de `[]` — devolver un array
 * donde el endpoint retorna un objeto enmascara la respuesta vacía.
 */
async function parsePublicJson<T>(resp: Response): Promise<T> {
  const raw = await resp.text();
  if (!resp.ok) {
    let errorMessage = `Error HTTP ${resp.status}`;
    try {
      const d = JSON.parse(raw);
      if (d?.message) errorMessage = d.message;
      else if (d?.error) errorMessage = d.error;
    } catch {
      errorMessage = raw || errorMessage;
    }
    throw new Error(errorMessage);
  }
  if (!raw || raw.trim().length === 0) return undefined as unknown as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return raw as unknown as T;
  }
}

/**
 * POST público al backend nuevo: sin Bearer y sin exigir sesión.
 *
 * Para páginas que se abren desde un link de email (ej. `/cert-action`), donde la
 * credencial es un token de un solo uso que viaja en el body — no la sesión de la
 * psicóloga. Justamente por eso no manda Authorization: si el navegador tiene guardado
 * un token de otra sesión, el backend no debe resolver identidad a partir de él.
 */
export async function bePublicPost<T, B = unknown>(
  path: string,
  body?: B,
  options?: { timeout?: number },
): Promise<T> {
  const controller = createTimeoutController(
    options?.timeout ?? API_CORE_CONFIG.TIMEOUT,
  );
  const resp = await fetch(`${PSI_PLATFORM_BE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
    signal: controller.signal,
  });
  return parsePublicJson<T>(resp);
}

/** Cliente HTTP tipado para el backend nuevo (beHttp). */
export const beHttp = {
  get: beGet,
  post: bePost,
  put: bePut,
  patch: bePatch,
  delete: beDelete,
  /** Sin sesión — sólo para páginas públicas. Ver `bePublicPost`. */
  publicPost: bePublicPost,
};
